import NextLink from "next/link";
import React from "react";
import { useRouter } from "next/router";
import { useFragment, type FragmentType } from "~/gql/runtime";
import { clearRouterPath, hasItems } from "~/features/app/utils";
import { omitGraphQLMetaProps } from "~/features/ui/dom-props";
import {
  requestQuoteModalIdFromHref,
  REQUEST_QUOTE_REV7_MODAL_ID,
  REQUEST_QUOTE_REV8_MODAL_ID,
} from "~/features/request-quote-dialog/modal";
import { parsePublicToLocalHref, fragment, stripTrailingSlash } from "./utils";

type NextLinkProps = Omit<React.ComponentProps<typeof NextLink>, "href" | "passHref" | "legacyBehavior" | "ref">;

export { REQUEST_QUOTE_REV7_MODAL_ID, REQUEST_QUOTE_REV8_MODAL_ID };

export type Props = FragmentType<typeof fragment> & {
  href?: string;
  text?: React.ReactNode;
  newWindow?: boolean;
  children?: React.ReactNode;
};

export type LinkProps = Props & NextLinkProps;

function normalizeProps<P extends LinkProps>(props: P) {
  const { text, children, newWindow, ...linkProps } = props;
  const { target, rel, ...rest } = linkProps;
  const domProps = omitGraphQLMetaProps(rest);

  return {
    rel: newWindow ? "noopener" : (rel as string),
    target: newWindow ? "_blank" : (target as string),
    children: hasItems(React.Children.toArray(children)) ? children : text,
    ...domProps,
  };
}

const scrollToElement = (hash: string) => {
  // Remove the # from the hash
  const elementId = hash.replace("#", "");
  const element = document.getElementById(elementId);

  if (element) {
    element.scrollIntoView({ behavior: "smooth" });
  }
};

export function Link(props: LinkProps) {
  const { href, ...link } = useFragment(fragment, props);
  const { children, rel, target, ...rest } = normalizeProps(link);
  const router = useRouter();

  if (!href) {
    return null;
  }

  const modalId = requestQuoteModalIdFromHref(href);

  if (modalId) {
    return (
      <button
        onClick={() => {
          router.push(
            {
              pathname: clearRouterPath(router.asPath),
              query: { ...router.query, modal: modalId },
            },
            undefined,
            { shallow: true, scroll: false }
          );
        }}
        {...rest}
      >
        {children}
      </button>
    );
  }

  // Workaround for
  // https://github.com/vercel/next.js/issues/44295#issuecomment-1621407494
  // https://github.com/vercel/next.js/pull/49521
  const handleClick = (e: React.UIEvent<HTMLAnchorElement>) => {
    if (!href.includes("#")) {
      return;
    }

    const [path = "", hash] = href.split("#");

    const currentPath = stripTrailingSlash(router.asPath.split(/[?#]/)[0] || "");
    const newPath = stripTrailingSlash(path);

    if (currentPath !== newPath) {
      return;
    }

    e.preventDefault();
    scrollToElement(`#${hash}`);
    window.history.pushState({}, "", href);
  };

  return (
    <NextLink
      href={parsePublicToLocalHref(href)}
      scroll={false}
      prefetch
      target={target}
      rel={rel}
      onClick={handleClick}
      onKeyDown={handleClick}
      role="link"
      {...rest}
    >
      {children}
    </NextLink>
  );
}
